import { css } from "@emotion/react";
import React, { useCallback, useMemo, useRef } from "react";
import dayjs from "dayjs";
import { PartialColorProps, useTheme } from "../theme";

export interface CalendarDayProps
  extends
    Omit<React.ComponentPropsWithoutRef<"div">, "color">,
    PartialColorProps {
  date: dayjs.ConfigType;
  hasChildren?: boolean;
  disableAnimation?: boolean;
  disabled?: boolean;
  variant?: "filled" | "outlined";
  event?: {
    start?: dayjs.ConfigType;
    end?: dayjs.ConfigType;
    type?: string;
  };
  children?: React.ReactNode;
  /***
   * 日付がクリックされた場合に、その日付とイベントを返す。
   */
  onClickDay?: (
    value: Date,
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => void;
}

export const CalendarDay = ({
  date,
  children,
  color = "default",
  hasChildren = false,
  variant = "outlined",
  disableAnimation = false,
  disabled: disabledProps,
  event,
  onClickDay,
  ...rest
}: CalendarDayProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const day = useMemo(() => {
    const d = dayjs(date);
    if (d.isValid()) return d;
    return dayjs();
  }, [date]);

  const theme = useTheme();
  const { palette, transitions } = theme;

  // eslint-disable-next-line @typescript-eslint/prefer-optional-chain, @typescript-eslint/prefer-nullish-coalescing
  const disabled = disabledProps || (event && event.type === "disabled");

  const dateStr = useMemo(() => {
    return day.format("YYYY-MM-DD");
  }, [day]);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (onClickDay) {
        onClickDay(day.toDate(), event);
      }
    },
    [day, onClickDay],
  );

  return (
    <div
      ref={ref}
      data-event-date={dateStr}
      aria-disabled={disabled}
      data-variant={variant}
      data-disable-animation={disableAnimation}
      css={css`
        position: relative;
        box-sizing: border-box;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 0px;
        overflow: hidden;
        touch-action: manipulation;
        /** decoration */
        cursor: pointer;
        user-select: none;
        border: none;
        outline: 0px;
        appearance: none;
        &::selection {
          color: inherit;
          background-color: transparent;
        }
        &[aria-disabled="true"] {
          color: ${palette.action.disabled} !important;
          cursor: not-allowed;
          background-color: ${palette.gray[200]} !important;
          box-shadow: 0px 0px 0px 0.5px ${palette.action.disabled} inset !important;
        }
        &[data-variant="filled"] {
          color: ${color === "default"
            ? palette.text.primary
            : palette.intentions[color].contrastText};
          background-color: ${color === "default"
            ? palette.background.paper
            : palette.intentions[color].main};
          box-shadow: 0px 0px 0px 0.5px
            ${color === "default"
              ? palette.action.disabled
              : palette.intentions[color].main}
            inset;
        }
        &[data-variant="outlined"] {
          color: ${color === "default"
            ? palette.text.primary
            : palette.intentions[color].main};
          box-shadow: 0px 0px 0px 0.5px
            ${color === "default"
              ? palette.action.disabled
              : palette.intentions[color].main}
            inset;
        }
        &[data-disable-animation="false"] {
          transition: background-color ${transitions.duration.shortest}
            ${transitions.easing.easeInOut};
          &:hover {
            background-color: ${color === "default"
              ? palette.gray[300]
              : palette.intentions[color].light};
          }
        }
      `}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      {...rest}
    >
      <div
        data-has-children={hasChildren}
        css={[
          hasChildren
            ? theme.typography.INTERNALS_DO_NOT_USE_calendarDay_hasChildren
            : theme.typography.INTERNALS_DO_NOT_USE_calendarDay,
          css`
            &[data-has-children="true"] {
              position: absolute;
              display: grid;
              grid-template-areas: "." ".";
              grid-template-rows:
                auto
                1fr;
              grid-template-columns: 100%;
              align-self: stretch;
              width: 100%;
              height: 100%;
              & > *:first-of-type {
                margin: 10% 10% 0px;
                text-align: right;
              }
            }
          `,
        ]}
      >
        <div>{day.date()}</div>
        {hasChildren && children ? (
          <div
            css={css`
              position: relative;
            `}
          >
            {children}
          </div>
        ) : null}
      </div>
    </div>
  );
};
